import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Modal,
  Form,
  message,
  Popconfirm,
  Tag,
  Switch,
  InputNumber,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { businessApi, BusinessItem, BusinessListParams } from '../../../../../services/businessService';
import { useDict } from '../../../../../hooks/useDict';
import QueryArea, { QueryField } from '../../components/QueryArea';
import './Business.css';

const { Option } = Select;
const { TextArea } = Input;

const BusinessManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<BusinessItem[]>([]);
  const [searchParams, setSearchParams] = useState<BusinessListParams>({
    keyword: '',
    category: '',
    status: ''
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<BusinessItem | null>(null);
  const [currentLevel, setCurrentLevel] = useState<number>(1);
  const [form] = Form.useForm();

  // 级联选择相关状态
  const [cascadeOptions, setCascadeOptions] = useState<{
    processCategory: Array<{ dict_value: string; dict_label: string }>;
    businessItemType: Array<{ dict_value: string; dict_label: string }>;
  }>({
    processCategory: [],
    businessItemType: []
  });
  
  // 数据字典
  const { dictData: businessCategoryDict } = useDict('business_category');
  
  // 调试字典数据

  // 查询字段配置 - 只保留业务名称
  const queryFields: QueryField[] = [
    {
      key: 'keyword',
      label: '业务名称',
      type: 'input',
      placeholder: '请输入业务名称或描述',
      width: 300,
    },
  ];

  // 加载数据 - 使用useCallback优化，支持树形结构，不分页
  const loadData = useCallback(async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const params = {
        ...searchParams,
      };
      
      const response = await businessApi.getAll(params);
      if (response.code === 200) {
        // 构建树形结构
        const treeData = buildBusinessTree(response.data || []);
        setDataSource(treeData);
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [searchParams, loading]);

  // 构建业务指南树形结构
  const buildBusinessTree = (data: BusinessItem[]) => {
    const map = new Map<number, BusinessItem>();
    const roots: BusinessItem[] = [];

    // 创建映射，初始时不设置children属性
    data.forEach(item => {
      map.set(item.id!, { ...item });
    });

    // 构建树形结构
    data.forEach(item => {
      const node = map.get(item.id!);
      if (node) {
        if (item.parentId) {
          const parent = map.get(item.parentId);
          if (parent) {
            parent.children = parent.children || [];
            parent.children.push(node);
          }
        } else {
          roots.push(node);
        }
      }
    });

    // 确保没有子节点的项目不显示展开图标
    const cleanChildren = (nodes: BusinessItem[]) => {
      nodes.forEach(node => {
        if (node.children && node.children.length === 0) {
          delete node.children;
        } else if (node.children) {
          cleanChildren(node.children);
        }
      });
    };

    cleanChildren(roots);
    return roots;
  };

  // 使用useRef来跟踪是否已经初始化
  const isInitialized = useRef(false);
  const lastSearchParams = useRef(searchParams);
  
  useEffect(() => {
    // 检查searchParams是否真正发生了变化
    const paramsChanged = JSON.stringify(searchParams) !== JSON.stringify(lastSearchParams.current);
    
    // 只在组件首次挂载时调用，或者当关键参数真正变化时调用
    if (!isInitialized.current || paramsChanged) {
      isInitialized.current = true;
      lastSearchParams.current = searchParams;
      loadData();
    }
  }, [searchParams]);

  // 搜索
  const handleSearch = () => {
    loadData();
  };

  // 重置
  const handleReset = () => {
    setSearchParams({ keyword: '', category: '', status: '' });
    setTimeout(loadData, 0);
  };

  // 处理查询参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams({
      keyword: values.keyword || '',
      category: values.category || '',
      status: values.status || '',
    });
  }, []);

  // 新增
  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setCurrentLevel(1);
    // 设置默认层级为1（一级业务指南）
    form.setFieldsValue({
      level: 1,
      status: '0',
      isHighlighted: '0',
      orderNum: 0,
    });
    setModalVisible(true);
  };

  // 添加子项
  const handleAddChild = async (parentRecord: BusinessItem) => {
    const newLevel = (parentRecord.level || 1) + 1;
    setEditingItem(null);
    form.resetFields();
    setCurrentLevel(newLevel);
    
    // 根据父级记录加载级联选项
    if (newLevel === 2) {
      // 一级新增子项，加载二级分类选项
      await loadCascadeOptions(parentRecord.type, 'business_category');
    } else if (newLevel === 3) {
      // 二级新增子项，加载三级项目类型选项
      await loadCascadeOptions(parentRecord.type, 'process_category');
    }
    
    form.setFieldsValue({
      parentId: parentRecord.id,
      level: newLevel,
      type: parentRecord.type,
      status: '0',
      isHighlighted: '0',
      orderNum: 0,
    });
    setModalVisible(true);
  };

  // 加载级联选项
  const loadCascadeOptions = async (parentValue: string, dictType: string) => {
    try {
      const response = await businessApi.getCascadeDict(parentValue, dictType);
      if (response.data) {
        if (dictType === 'business_category') {
          setCascadeOptions(prev => ({
            ...prev,
            processCategory: response.data
          }));
        } else if (dictType === 'process_category') {
          setCascadeOptions(prev => ({
            ...prev,
            businessItemType: response.data
          }));
        }
      }
    } catch (error) {
      console.error('加载级联选项失败:', error);
    }
  };

  // 处理业务名称选择变化（级联选择）
  const handleNameChange = async (value: string, level: number) => {
    if (level === 1) {
      // 一级选择变化，加载对应的二级分类
      await loadCascadeOptions(value, 'business_category');
      // 清空三级选项
      setCascadeOptions(prev => ({
        ...prev,
        businessItemType: []
      }));
    } else if (level === 2) {
      // 二级选择变化，加载对应的三级项目类型
      await loadCascadeOptions(value, 'process_category');
    }
  };

  // 编辑
  const handleEdit = (record: BusinessItem) => {
    setEditingItem(record);
    setCurrentLevel(record.level || 1);
    // 处理Switch组件的值转换
    const formValues = {
      ...record,
      status: record.status === '0',
      isHighlighted: record.isHighlighted === '1',
    };
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  // 删除
  const handleDelete = async (id: number) => {
    try {
      await businessApi.delete(id);
      message.success('删除成功');
      loadData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async (ids: number[]) => {
    try {
      await businessApi.batchDelete(ids);
      message.success('批量删除成功');
      loadData();
    } catch (error) {
      message.error('批量删除失败');
    }
  };

  // 保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = { ...values };
      
      // 处理Switch组件的值转换
      data.status = data.status ? '0' : '1';
      data.isHighlighted = data.isHighlighted ? '1' : '0';
      
      // 对于三级业务指南，将name字段的值作为type字段
      if (data.level === 3) {
        data.type = data.name;
      }
      
      if (editingItem) {
        await businessApi.update({ ...editingItem, ...data });
        message.success('修改成功');
      } else {
        await businessApi.create(data);
        message.success('新增成功');
      }
      setModalVisible(false);
      loadData();
    } catch (error) {
      message.error('保存失败');
    }
  };

  // 表格列定义 - 简化显示
  const columns = [
    {
      title: '业务名称',
      dataIndex: 'name',
      key: 'name',
      width: '35%',
      render: (text: string, record: BusinessItem) => {
        const levelIcon = record.level === 1 ? '📁' : record.level === 2 ? '📋' : '📝';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 8, fontSize: '16px' }}>{levelIcon}</span>
            <span>{text}</span>
          </div>
        );
      },
    },
    {
      title: '业务描述',
      dataIndex: 'description',
      key: 'description',
      width: '40%',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      width: '25%',
      render: (_: any, record: BusinessItem) => (
        <Space size={4} wrap>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ padding: '2px 4px', fontSize: '12px' }}
          >
            编辑
          </Button>
          {record.level && record.level < 3 && (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleAddChild(record)}
              style={{ padding: '2px 4px', fontSize: '12px' }}
            >
              新增子项
            </Button>
          )}
          {record.level !== 1 && (
            <Popconfirm
              title="确定要删除吗？"
              onConfirm={() => handleDelete(record.id!)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="business-management">
      {/* 统一查询区域 */}
      <QueryArea
        fields={queryFields}
        values={searchParams}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleAdd}
        addButtonText="新增"
      />

      {/* 数据表格 */}
      <div className="table-card">
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          expandable={{
            expandRowByClick: false,
            childrenColumnName: 'children',
          }}
          pagination={false}
        />
      </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingItem ? '编辑业务指南' : '新增业务指南'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          setTimeout(() => {
            form.resetFields();
            setEditingItem(null);
          }, 300);
        }}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form 
          form={form} 
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          preserve={false}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="name"
              label="业务名称"
              rules={[{ required: true, message: '请输入业务名称' }]}
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              {/* 根据层级动态显示不同的输入组件 */}
              {currentLevel === 1 ? (
                <Select 
                  placeholder="请选择业务分类" 
                  disabled={editingItem !== null}
                  onChange={(value) => handleNameChange(value, 1)}
                >
                  {businessCategoryDict?.map(item => (
                    <Option key={item.dictValue} value={item.dictValue}>
                      {item.dictLabel}
                    </Option>
                  ))}
                </Select>
              ) : currentLevel === 2 ? (
                <Select 
                  placeholder="请选择流程分类" 
                  disabled={editingItem !== null}
                  onChange={(value) => handleNameChange(value, 2)}
                >
                  {cascadeOptions.processCategory?.map(item => (
                    <Option key={item.dict_value} value={item.dict_value}>
                      {item.dict_label}
                    </Option>
                  ))}
                </Select>
              ) : currentLevel === 3 ? (
                <Select 
                  placeholder="请选择项目类型" 
                  disabled={editingItem !== null}
                >
                  {cascadeOptions.businessItemType?.map(item => (
                    <Option key={item.dict_value} value={item.dict_value}>
                      {item.dict_label}
                    </Option>
                  ))}
                </Select>
              ) : (
                <Input placeholder="请输入业务名称" />
              )}
            </Form.Item>

            <Form.Item 
              name="orderNum" 
              label="排序"
              rules={[
                { required: true, message: '请输入排序' },
                { type: 'number', min: 0, message: '排序必须为非负整数' }
              ]}
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <InputNumber 
                min={0} 
                precision={0}
                placeholder="请输入排序" 
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
          
          <Form.Item name="description" label="业务描述">
            <TextArea rows={3} placeholder="请输入业务描述" />
          </Form.Item>

          {/* 根据层级显示不同字段 */}
          {currentLevel === 3 && (
            <>
              <Form.Item name="content" label="详细内容">
                <TextArea rows={4} placeholder="请输入详细内容" />
              </Form.Item>
              
              <div style={{ display: 'flex', gap: '16px' }}>
                <Form.Item 
                  name="estimatedTime" 
                  label="预计时间"
                  style={{ flex: 1 }}
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <Input placeholder="如：1-3个工作日" />
                </Form.Item>
                <Form.Item 
                  name="stepOrder" 
                  label="步骤顺序"
                  style={{ flex: 1 }}
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <InputNumber 
                    min={1} 
                    precision={0}
                    placeholder="步骤顺序" 
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item 
              name="status" 
              label="状态" 
              valuePropName="checked"
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Switch 
                checkedChildren="正常" 
                unCheckedChildren="停用"
                defaultChecked={true}
              />
            </Form.Item>
            <Form.Item 
              name="isHighlighted" 
              label="是否高亮" 
              valuePropName="checked"
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Switch 
                checkedChildren="是" 
                unCheckedChildren="否"
                defaultChecked={false}
              />
            </Form.Item>
          </div>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BusinessManagement;
